import React, { useEffect, useState } from "react";
import { Download, Share } from "lucide-react";
import { toast } from "sonner";

/**
 * Install app button — Progressive Web App (PWA)
 *
 * On Chrome/Edge/Android: triggers the browser's install prompt (uses the
 * `beforeinstallprompt` event captured in index.html into `window.__pwaPrompt`).
 * On iOS Safari: iOS never fires that event, so we show an "Add to Home Screen"
 * instructions modal.
 * When the app is already installed (running in standalone mode), the button
 * hides itself so we don't nag the user.
 */
export default function InstallAppButton({ variant = "primary", className = "" }) {
  const [canInstall, setCanInstall] = useState(!!window.__pwaPrompt);
  const [installed, setInstalled] = useState(
    window.matchMedia && window.matchMedia("(display-mode: standalone)").matches
  );
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    const onInstallable = () => setCanInstall(true);
    const onInstalled = () => { setCanInstall(false); setInstalled(true); toast.success("App installed 🎉"); };
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  if (installed) return null;

  const ua = navigator.userAgent || "";
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isStandalone = window.navigator.standalone === true;
  if (isStandalone) return null;

  const promptInstall = async () => {
    if (window.__pwaPrompt) {
      window.__pwaPrompt.prompt();
      try {
        const { outcome } = await window.__pwaPrompt.userChoice;
        if (outcome === "accepted") {
          setInstalled(true);
          toast.success("App installed 🎉");
        }
      } catch {
        /* user dismissed */
      }
      window.__pwaPrompt = null;
      setCanInstall(false);
    } else if (isIos) {
      setShowIosHelp(true);
    } else {
      // Fallback for browsers that don't expose beforeinstallprompt
      // (Firefox on desktop, some Samsung Internet builds, etc.)
      toast.info("Use your browser's menu → Install / Add to Home Screen");
    }
  };

  const showButton = canInstall || isIos || true; // always show (with fallback message)
  if (!showButton) return null;

  const base = variant === "primary" ? "btn-primary" : "btn-ghost";
  return (
    <>
      <button
        type="button"
        onClick={promptInstall}
        className={`${base} inline-flex items-center gap-1.5 ${className}`}
        data-testid="install-app-btn"
        title="Install as an app"
      >
        <Download size={14} />
        <span>Install app</span>
      </button>

      {showIosHelp && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setShowIosHelp(false)}>
          <div className="card-surface p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="ios-install-help">
            <div className="flex items-center gap-2 mb-2">
              <Share size={18} className="text-[#C45A41]" />
              <h3 className="font-display font-bold text-lg">Install on iPhone</h3>
            </div>
            <ol className="text-sm text-[#2C2B29] space-y-2 pl-5 list-decimal">
              <li>Tap the <b>Share</b> icon <Share size={12} className="inline mx-0.5" /> at the bottom of Safari.</li>
              <li>Scroll and tap <b>&ldquo;Add to Home Screen&rdquo;</b>.</li>
              <li>Tap <b>Add</b> in the top-right. Marsh will appear as an app icon.</li>
            </ol>
            <p className="text-xs text-[#75706A] mt-3">
              This only works in Safari on iOS (Chrome/Firefox on iOS use the same instructions).
            </p>
            <div className="flex justify-end mt-4">
              <button className="btn-primary text-sm" onClick={() => setShowIosHelp(false)} data-testid="ios-install-close">Got it</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
